import React, { useState, useEffect, useRef } from 'react';
import { Customer360Detail } from '../../services/customer360DetailApi';
import { toast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';

interface Message {
  id: string;
  customer_id: string;
  channel: 'whatsapp' | 'instagram';
  direction: 'inbound' | 'outbound';
  content: string;
  timestamp: string;
  status?: 'sent' | 'delivered' | 'read' | 'failed';
}

interface CustomerChatProps {
  customer: Customer360Detail;
  onClose?: () => void;
}

export default function CustomerChat({ customer, onClose }: CustomerChatProps) {
  const [channel, setChannel] = useState<'whatsapp' | 'instagram'>('whatsapp');
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadMessages();
    // Polling para novas mensagens a cada 5 segundos
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  }, [customer.id, channel]);

  const loadMessages = async () => {
    try {
      const whatsappKey = localStorage.getItem('whatsapp_api_key');
      const instagramKey = localStorage.getItem('instagram_api_key');
      
      if (!whatsappKey && !instagramKey) {
        setLoading(false);
        return;
      }

      const apiKey = channel === 'whatsapp' ? whatsappKey : instagramKey;
      if (!apiKey) {
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL || 'https://supabase.terenceconsultoria.com.br'}/functions/v1/messages?customer_id=${customer.id}&channel=${channel}`,
        {}
      );

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    const whatsappKey = localStorage.getItem('whatsapp_api_key');
    const instagramKey = localStorage.getItem('instagram_api_key');
    const apiKey = channel === 'whatsapp' ? whatsappKey : instagramKey;

    if (!apiKey) {
      toast({
        title: 'Configuração necessária',
        description: `Configure a chave de API do ${channel === 'whatsapp' ? 'WhatsApp' : 'Instagram'} em Settings`,
        variant: 'destructive',
      });
      return;
    }

    if (!customer.phone && channel === 'whatsapp') {
      toast({
        title: 'Telefone ausente',
        description: 'Cliente não possui telefone cadastrado para WhatsApp.',
        variant: 'destructive',
      });
      return;
    }

    setSending(true);
    const messageContent = newMessage.trim();
    setNewMessage('');

    // Otimistic update
    const tempMessage: Message = {
      id: `temp-${Date.now()}`,
      customer_id: customer.id,
      channel,
      direction: 'outbound',
      content: messageContent,
      timestamp: new Date().toISOString(),
      status: 'sent',
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL || 'https://supabase.terenceconsultoria.com.br'}/functions/v1/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customer_id: customer.id,
            channel,
            content: messageContent,
            recipient: channel === 'whatsapp' ? customer.phone : customer.email,
            api_key: apiKey,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Substituir mensagem temporária pela real
        setMessages(prev => prev.map(msg => 
          msg.id === tempMessage.id ? data.message : msg
        ));
      } else {
        throw new Error('Erro ao enviar mensagem');
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      // Remover mensagem temporária em caso de erro
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
      toast({
        title: 'Erro ao enviar mensagem',
        description: 'Tente novamente.',
        variant: 'destructive',
      });
      setNewMessage(messageContent);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Agora';
    if (minutes < 60) return `${minutes}m atrás`;
    if (hours < 24) return `${hours}h atrás`;
    if (days < 7) return `${days}d atrás`;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const whatsappKey = localStorage.getItem('whatsapp_api_key');
  const instagramKey = localStorage.getItem('instagram_api_key');

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="truncate">Chat</CardTitle>
            <CardDescription className="truncate">
              {customer.full_name || customer.email || customer.id}
            </CardDescription>
          </div>
          {onClose ? (
            <Button variant="outline" size="sm" onClick={onClose}>
              Fechar
            </Button>
          ) : null}
        </div>

        <Tabs value={channel} onValueChange={(v) => setChannel(v as any)}>
          <TabsList className="w-full">
            <TabsTrigger value="whatsapp" disabled={!whatsappKey}>
              WhatsApp {!whatsappKey ? <span className="ml-2 text-xs">(sem chave)</span> : null}
            </TabsTrigger>
            <TabsTrigger value="instagram" disabled={!instagramKey}>
              Instagram {!instagramKey ? <span className="ml-2 text-xs">(sem chave)</span> : null}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>

      <CardContent className="flex h-[calc(100%-110px)] flex-col gap-3">
        {(!whatsappKey && !instagramKey) ? (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            Configure as chaves de API do WhatsApp e/ou Instagram em Settings
          </div>
        ) : (!whatsappKey && channel === 'whatsapp') || (!instagramKey && channel === 'instagram') ? (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            Configure a chave de API do {channel === 'whatsapp' ? 'WhatsApp' : 'Instagram'} em Settings
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 rounded-lg border p-3">
              {loading ? (
                <div className="text-sm text-muted-foreground">Carregando mensagens...</div>
              ) : messages.length === 0 ? (
                <div className="text-sm text-muted-foreground">Nenhuma mensagem ainda. Inicie a conversa.</div>
              ) : (
                <div className="space-y-3">
                  {messages.map((message) => {
                    const outbound = message.direction === 'outbound';
                    return (
                      <div key={message.id} className={outbound ? 'flex justify-end' : 'flex justify-start'}>
                        <div
                          className={[
                            'max-w-[85%] rounded-2xl px-3 py-2 text-sm',
                            outbound ? 'bg-primary text-primary-foreground' : 'bg-accent text-foreground',
                          ].join(' ')}
                        >
                          <div className="whitespace-pre-wrap">{message.content}</div>
                          <div className="mt-1 flex items-center justify-end gap-2 text-[11px] opacity-80">
                            <span>{formatTime(message.timestamp)}</span>
                            {outbound && message.status ? (
                              <Badge variant="secondary" className="h-5 px-2 text-[10px]">
                                {message.status === 'sent' && 'enviado'}
                                {message.status === 'delivered' && 'entregue'}
                                {message.status === 'read' && 'lido'}
                                {message.status === 'failed' && 'falhou'}
                              </Badge>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            <div className="flex items-end gap-2">
              <Textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={`Digite sua mensagem para ${channel === 'whatsapp' ? 'WhatsApp' : 'Instagram'}...`}
                rows={3}
                disabled={sending}
              />
              <Button onClick={sendMessage} disabled={!newMessage.trim() || sending} className="h-10">
                {sending ? 'Enviando…' : 'Enviar'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

